import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

/**
 * AuthCallback — handles the redirect from auth.emergentagent.com.
 * URL fragment: `#session_id=...`
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const user = await loginWithGoogle(sessionId);
        toast.success(`Welcome, ${user.name}`);
        navigate(user.role === "admin" ? "/admin" : "/", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || "Google sign-in failed";
        setError(typeof detail === "string" ? detail : "Google sign-in failed");
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed");
        setTimeout(() => navigate("/login", { replace: true }), 1500);
      }
    })();
  }, [location.hash, loginWithGoogle, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--inmind-bg)" }} data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--inmind-primary)] mx-auto" />
        <p className="text-sm text-[var(--inmind-text-secondary)] mt-3">
          {error ? error : "Signing you in…"}
        </p>
      </div>
    </div>
  );
}
